import { ImageName } from "@mcleod/images";
import { BlurEvent, Button, Checkbox, DataSourceMode, DropdownItem, Image, Label, Layout, SlideoutDecorator, TableRow, Textbox, Toast, ValidationResult } from "@mcleod/components";
import { ActionPermission, Api, DatePart, DateUtil, ModelRow, PermissionsUtil, StringUtil } from "@mcleod/core";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutYSplit } from "./autogen/AutogenLayoutYSplit";
import { StopEntry } from "./StopEntry";
import { YSplitStopsTable } from "./YSplitStopsTable";

interface YSplitSlideoutProps {
    orderId: string;
    isCompanyBoth: boolean;
    movementId?: string;
    manifestId?: string;
    movementSequence?: string;
    leaveFreight?: boolean;
    crossDock?: boolean;
    doAfterSlideOut?: (success: boolean) => void;
}

// This information from the movement is used to determine whether the movement can be split
interface YSplitValidationData {
    brokerage: boolean;
    ltl: boolean;
    loaded: boolean;
    status: string;
    transferredToSettlements: boolean;
}

const actionButtonProps = {
    caption: "Save",
    backgroundColor: "primary",
    color: "primary.reverse",
    busyWhenDataSourceBusy: true,
    paddingTop: 10,
    paddingBottom: 10,
    rowBreak: false,
    borderWidth: 0,
    minWidth: 128
}

const STOP_DELIVERED: string = "D";

export class YSplit extends AutogenLayoutYSplit {
    public actionButton: Button;
    private _stopType: string = "SD";
    private _success: boolean = false;
    public dateEdited: boolean = false;
    private orderId: string;
    public static readonly PICKUP_STOP_TYPES: string[] = ["PU", "SP"];
    public static readonly DELIVERY_STOP_TYPES: string[] = ["SO", "SD", "AD"];
    static readonly PICKUP_VALID_MESSAGE = "Scheduled Split Drop date cannot be later than the Scheduled Split Pickup date.";

    static setImage(origin_stop_type: string, dest_stop_type: string, image: Image) {
        const imageName = origin_stop_type === "SP" && dest_stop_type === "SD" ? ImageName.ysplitComplete
        : origin_stop_type === "SP" ? ImageName.ysplitPick : dest_stop_type === "SD" ? ImageName.ysplit : null;
        const color = origin_stop_type === "SP" && dest_stop_type === "SD" ? "primary"
        : origin_stop_type === "SP" ? "success" : dest_stop_type === "SD" ? "primary" : null;
        image.name = imageName;
        image.color = color;
    }

    public static validateYSplit(data: YSplitValidationData): boolean {
        if (data == null) {
            return false;
        }

        if (data.ltl) {
            CommonDialogs.showDialog("Cannot split LTL movements.");
            return false;
        }

        if (!data.loaded) {
            CommonDialogs.showDialog("Cannot split empty movements.");
            return false;
        }

        if (data.status == null) {
            CommonDialogs.showDialog("Error: Cannot split a movement without a status.");
            return false;
        }

        if (data.status === "D") {
            if (PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_SPLIT_DELIVERED_MOVE) &&
                PermissionsUtil.isUserDeniedAction(ActionPermission.ACTION_UPDATE_DEL_MOVES)) {
                CommonDialogs.showDialog("You do not have permission to split delivered movements.");
                return false;
            } else if (data.transferredToSettlements) {
                CommonDialogs.showDialog("This movement has already been transferred to settlements. No delivered Y-split will be allowed.");
                return false;
            }
        } else if (data.status === "V") {
            CommonDialogs.showDialog("Cannot split a voided movement.");
            return false;
        }

        return true;
    }

    sourceStopAfterModeChange() {
        this.activeRow.set("stop_type", this.stopType);
    }

    public static showSlideout(props: YSplitSlideoutProps) {
        const layout = Layout.getLayout("lme/dispatch/YSplit") as YSplit;
        const actionButton = new Button({...actionButtonProps});
        const title = layout.setupSlideoutTitle(props);

        new SlideoutDecorator({
            layout,
            width: '90%',
            fillHeight: true,
            title: title,
            displayOverlay: true,
            overlayProps: { closeOnClickOff: false, greyedBackground: true },
            addlComponents: [actionButton],
            layoutLoadListeners: () => {
                layout.orderId = props.orderId;
                layout.mainDataSource.mode = DataSourceMode.ADD;
                layout.setupActionButton(actionButton, props);
                layout.setupStopEntry(layout.layoutStopEntry);
                layout.setupYSplitStopsTable(layout.layoutYSplitStopsTable, props);
                if (props.isCompanyBoth) {
                    layout.panelNewMovementType.visible = true;
                }
            },
            doAfterSlideOut: () => {
                props.doAfterSlideOut(layout.success);
            },
        });
    }

    setupSlideoutTitle(props: YSplitSlideoutProps): string {
        let title = "Create Y-Split - Order " + props.orderId
        if (props.manifestId && !props.leaveFreight && !props.crossDock)
             title = "Create Y-Split - Manifest " + props.manifestId;
        else if (props.leaveFreight)
            title = "Leave Freight - Manifest " + props.manifestId;
        else if (props.crossDock)
            title = "Cross Dock - Manifest " + props.manifestId;
        return title;
    }

    onLoad(): void | Promise<void> {
        this.layoutStopEntry.panelAdditionalDetails.visible = false;
    }

    async setupYSplitStopsTable(layout: YSplitStopsTable, props: YSplitSlideoutProps) {
        const isManifest = !!props.manifestId;
        const searchParam =  props.manifestId ? { "movement_id": props.movementId, "is_manifest": true} : {"order_id": props.orderId};
        await layout.sourceStop.search(searchParam);
        layout.isManifest = isManifest;
        layout.leaveFreight = props.leaveFreight;
        layout.crossDock = props.crossDock;
        layout.orderId = props.orderId;
        layout.onStopSelected = (modelRow : ModelRow) => {
            this.switchBrokerageSplit.checked = modelRow.get("movement.brokerage") === "Y";
        }
        for (const modelRow of layout.tableOrderStops.data) {
            const stopType = modelRow.get("stop_type");
            const status = modelRow.get("status");
            const tableRow = layout.tableOrderStops.findTableRow(modelRow);
            await tableRow.populateDOMIfNeeded();
            if ( YSplit.DELIVERY_STOP_TYPES.includes(stopType) && status !== STOP_DELIVERED) {
                layout.selectStop(tableRow);
                break;
            }
            else if (STOP_DELIVERED === status) {
                const buttonStopSelected = tableRow.findComponentById("buttonStopSelected") as Button;
                buttonStopSelected.enabled = false;
                buttonStopSelected.imageColor = "default.lightest";
                buttonStopSelected.disabledTooltip = "Unable to split drop before departed pickup.";
            }
        }
    }

    setupActionButton(actionButton: Button, props: YSplitSlideoutProps) {
        this.actionButton = actionButton;
        this.actionButton.dataSource = this.mainDataSource;
        this.actionButton.addClickListener(() => {
            if (props.leaveFreight === true) {
                this.createLeaveFreight();
            }
            else if (props.crossDock === true) {
                this.createCrossDock();
            }
            else
                this.createYSplit();
        });
    }

    setupStopEntry(layout: StopEntry) {
        layout.textboxStopType.color = "primary";
        layout.textboxStopType.fontSize = "xlarge";
        layout.textboxStopType.printable = true;
        layout.textboxPickupDate.visible = false;
        layout.labelBetween.visible = false;
        layout.textboxSchedArriveLate.required = false;
        layout.panelActualTimes.visible = false;
        layout.textboxContactName.lookupModelLayoutHeight = 200;
        layout.textboxSchedArriveEarly.caption = "Scheduled Split Drop";
        layout.textboxSchedArriveLate.caption = "Scheduled Split Pickup";
        layout.newEntry().then(() => {
            layout.activeRow.set("stop_type", "SD");
            layout.textboxStopType.selectedItem = new DropdownItem("SD", "Split Drop");
        });
        layout.textboxSchedArriveEarly.addBlurListener( (event: BlurEvent)=>{
            if(event.changedWhileFocused && layout.textboxSchedArriveLate.isEmpty() && !this.dateEdited){
                layout.activeRow.set("sched_arrive_late", layout.textboxSchedArriveEarly.getDataValue());
                this.dateEdited = true;
            }
            if(layout.textboxSchedArriveEarly.isEmpty()){
                this.dateEdited = false;
            }
        });
        layout.addLayoutLoadListener(()=>{
            layout.textboxSchedArriveEarly.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                return this.dateValidator(true, layout.textboxSchedArriveEarly, layout.textboxSchedArriveLate,
                YSplit.PICKUP_VALID_MESSAGE, mode);
            };
            layout.textboxSchedArriveLate.addlValidationCallback = (_value: string, mode: DataSourceMode): ValidationResult => {
                return this.dateValidator(false, layout.textboxSchedArriveEarly, layout.textboxSchedArriveLate,
                YSplit.PICKUP_VALID_MESSAGE, mode);
            };
        });
    }

    async createYSplit() {
        const data = this.mainDataSource.data as ModelRow[];
        if (data) {
            this.actionButton.busy = true;
            try {
                if (this.layoutYSplitStopsTable.selectedStop == null) {
                    if (!this.mainDataSource.validateSimple()) {
                        CommonDialogs.showError("To save changes, a Y-Split stop must be selected and filled with the necessary information.");
                    } else {
                        CommonDialogs.showDialog([
                            new Label({ text: "Please select a", rowBreak: false }), new Image({ name: ImageName.add, color: "success", rowBreak: false }),
                            new Label({ text: "from the stops list."})
                        ], { title: "Error" });
                    }
                    return;
                }

                if (this.mainDataSource.validate()) {
                    const existingRows: ModelRow[] = this.getExistingRows();
                    if (existingRows.some((modelRow: ModelRow) => modelRow.hasChanged())) {
                        try {
                            const response = await Api.post("lme/dispatch/asset/reorganize-stops",
                                {
                                    "order_id": this.orderId,
                                    "stops": existingRows
                                });
                            if (!response?.data?.[0]?.success) {
                                CommonDialogs.showError("Unexpected problem updating existing stops.");
                                return;
                            }
                        } catch (e) {
                            CommonDialogs.showError(e);
                            return;
                        }
                    }

                    const targetStop: ModelRow = this.layoutYSplitStopsTable.selectedStop;
                    data[0].set("movement_id", targetStop.get("movement_id"));
                    const isBrokerageMovement = targetStop.get("movement.brokerage") === "Y";
                    const splitDifferent = isBrokerageMovement ? !this.switchBrokerageSplit.checked :
                            !isBrokerageMovement ? this.switchBrokerageSplit.checked : false;
                    this.layoutStopEntry.layoutStopNotesAndRefNbrs.saveChangesToRow(data[0]);
                    const response = await Api.post("lme/dispatch/create-ysplit", {
                        "new_stop": data,
                        "target_stop_id": targetStop.get("id"),
                        "split_different": splitDifferent
                    }).finally(() => this.actionButton.busy = false);
                    this.success = response.data[0]?.success ?? false;
                    if (!this.success) {
                        const message = response.data[0]?.message ?? "Could not create Y-split";
                        CommonDialogs.showError(message, "Error creating Y-split");
                    } else {
                        this.slideOut();
                    }
                }
            } finally {
                this.actionButton.busy = false;
            }
        }
    }

    async createLeaveFreight() {
        const data = this.mainDataSource.data as ModelRow[];
        if (data) {
            this.actionButton.busy = true;
            try {
                if (this.layoutYSplitStopsTable.selectedStop == null) {
                    if (!this.mainDataSource.validateSimple()) {
                        CommonDialogs.showError("To save changes, a Y-Split stop must be selected and filled with the necessary information.");
                    } else {
                        CommonDialogs.showDialog([
                            new Label({ text: "Please select a", rowBreak: false }), new Image({ name: ImageName.add, color: "success", rowBreak: false }),
                            new Label({ text: "from the stops list."})
                        ], { title: "Error" });
                    }
                    return;
                }
                const selectedOrders = [];
                this.layoutYSplitStopsTable.tableOrderStops.rows.forEach( (row: TableRow) => {
                    const checkboxLeaveFreight = row.findComponentById("checkboxLeaveFreight") as Checkbox;
                    if (checkboxLeaveFreight != null && checkboxLeaveFreight.checked) {
                        const modelRow = row.data as ModelRow;
                        selectedOrders.push({"stop_id": modelRow.get("id"), "order_id": modelRow.get("order_id"), "movement_sequence": modelRow.get("movement_sequence")});
                    }
                });
                const selectedStop: ModelRow = this.layoutYSplitStopsTable.selectedStop;
                data[0].set("movement_id", selectedStop.get("movement_id"));
                const response = await Api.post("lme/dispatch/partial-shipment-leave-freight-cross-dock", {
                    "new_stop": data,
                    "selected_orders": selectedOrders,
                    "target_stop_id": selectedStop.get("id"),
                    "cross_dock": false
                }).finally(() => this.actionButton.busy = false);
                this.success = response.data[0]?.success ?? false;
                if (!this.success) {
                    const message = response.data[0]?.message ?? "Could not create Leave Freight";
                    CommonDialogs.showError(message, "Error creating Leave Freight");
                } else {
                    this.slideOut();
                    Toast.showToast("Leave Freight created successfully.");
                }
            } finally {
                this.actionButton.busy = false;
            }
        }
    }

    async createCrossDock() {
        const data = this.mainDataSource.data as ModelRow[];
        if (data) {
            this.actionButton.busy = true;
            try {
                const selectedStop: ModelRow = this.layoutYSplitStopsTable.selectedStop;
                if (selectedStop == null) {
                    CommonDialogs.showError("No stop selected for cross dock.");
                    return;
                }
                const beforeStops = [];
                const afterStops =[];
                const selectedMoveSequence = selectedStop.data["movement_sequence"];
                this.layoutYSplitStopsTable.tableOrderStops.rows.forEach((row: TableRow) => {
                    const sequence = row.data.get("movement_sequence");
                    const modelRow = row.data as ModelRow;
                    const stopType = row.data.get("stop_type");
                    if (sequence < selectedMoveSequence) {
                        beforeStops.push({
                            "stop_id": modelRow.get("id"),
                            "order_id": modelRow.get("order_id"),
                            "movement_sequence": modelRow.get("movement_sequence"),
                            "stop_type": stopType
                        });
                    }
                    else if (sequence >= selectedMoveSequence) {
                        afterStops.push({
                            "stop_id": modelRow.get("id"),
                            "order_id": modelRow.get("order_id"),
                            "movement_sequence": modelRow.get("movement_sequence"),
                            "stop_type": stopType
                        });
                    }
                });
                const finalStopsToCrossDocks = [];
                // Create a map for afterStops keyed by order_id
                const afterStopsMap = new Map<string, any>();
                for (const afterStop of afterStops) {
                    afterStopsMap.set(afterStop["order_id"].toLowerCase(), afterStop);
                }
                data[0].set("movement_id", selectedStop.get("movement_id"));
                // Iterate through beforeStops and find matches
                for (const beforeStop of beforeStops) {
                    if (YSplit.PICKUP_STOP_TYPES.includes(beforeStop["stop_type"])) {
                        const matchingAfterStop = afterStopsMap.get(beforeStop["order_id"].toLowerCase());
                        if (matchingAfterStop) {
                            finalStopsToCrossDocks.push(matchingAfterStop);
                        }
                    }
                }
                //check before stop and find related after stops.
                const response = await Api.post("lme/dispatch/partial-shipment-leave-freight-cross-dock", {
                    "new_stop": data,
                    "selected_orders": finalStopsToCrossDocks,
                    "target_stop_id": selectedStop.get("id"),
                    "cross_dock": true
                }).finally(() => this.actionButton.busy = false);
                this.success = response.data[0]?.success ?? false;
                if (!this.success) {
                    const message = response.data[0]?.message ?? "Could not create Cross Dock";
                    CommonDialogs.showError(message, "Error creating Cross Dock");
                } else {
                    this.slideOut();
                    Toast.showToast("Cross Dock created successfully.");
                }
            }
            finally {
                this.actionButton.busy = false;
            }
        }
    }

    private dateValidator(isPickup: boolean, pickupComp: Textbox, dropComp: Textbox, message: string,
        mode: DataSourceMode): ValidationResult {
        if (mode === DataSourceMode.SEARCH) {
            return null;
        }
        if (!StringUtil.isEmptyString(pickupComp.text) && !StringUtil.isEmptyString(dropComp.text)) {
            const arrivalDate = DateUtil.parseDateWithKeywords(pickupComp.text, true, true);
            const departureDate = DateUtil.parseDateWithKeywords(dropComp.text, true, true);
            const dayDiff = DateUtil.dateDiff(DatePart.SECOND, departureDate, arrivalDate);

            if (dayDiff < 0) {
                return {
                    component: isPickup ? pickupComp : dropComp,
                    isValid: false,
                    validationMessage: message
                };
            } else {
                pickupComp.resetValidation();
                dropComp.resetValidation();
            }
        }
        return null;
    }

    getExistingRows() {
        return this.layoutYSplitStopsTable.tableOrderStops.data.filter((modelRow: ModelRow) => modelRow._appending == false);
    }

    public get stopType(): string {
        return this._stopType;
    }

    public set stopType(value: string) {
        this._stopType = value;
    }

    public get success(): boolean {
        return this._success;
    }

    public set success(value: boolean) {
        this._success = value;
    }
}
